import { Search, Building2, Globe, Users, MoreHorizontal, Filter, LayoutGrid, List, ArrowUpDown, Trash2, Pencil, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAccountsWithStats, useDeleteAccount } from "@/hooks/useAccounts";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useMemo } from "react";
import { AddAccountDialog } from "@/components/accounts/AddAccountDialog";
import { EditAccountDialog } from "@/components/accounts/EditAccountDialog";
import { AccountDetailSheet } from "@/components/accounts/AccountDetailSheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";

const sizeColors: Record<string, string> = {
  enterprise: "bg-purple-500/10 text-purple-400",
  mid_market: "bg-blue-500/10 text-blue-400",
  sme: "bg-cyan-500/10 text-cyan-400",
  micro: "bg-green-500/10 text-green-400",
};

const sizeLabels: Record<string, string> = {
  enterprise: "Enterprise",
  mid_market: "Mid-Market",
  sme: "SME",
  micro: "Micro",
};

type SortField = "name" | "total_value" | "contacts_count" | "created_at";
type SortOrder = "asc" | "desc";

export default function Accounts() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [sizeFilter, setSizeFilter] = useState<string>("all");
  const [industryFilter, setIndustryFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [showFilters, setShowFilters] = useState(false);
  
  // Edit/Delete/Detail states
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<any>(null);

  const { data: accounts, isLoading, error } = useAccountsWithStats();
  const deleteAccount = useDeleteAccount();

  // Get unique industries for filter
  const industries = useMemo(() => {
    if (!accounts) return [];
    const uniqueIndustries = [...new Set(accounts.map(a => a.industry).filter(Boolean))];
    return uniqueIndustries.sort();
  }, [accounts]);

  // Filter and sort accounts
  const filteredAccounts = useMemo(() => {
    if (!accounts) return [];
    
    let result = accounts.filter((account) => {
      // Search filter
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const matchesSearch = 
          account.name.toLowerCase().includes(search) ||
          account.industry?.toLowerCase().includes(search) ||
          account.website?.toLowerCase().includes(search) ||
          account.country?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Size filter
      if (sizeFilter !== "all" && account.size_band !== sizeFilter) return false;
      
      // Industry filter
      if (industryFilter !== "all" && account.industry !== industryFilter) return false;
      
      return true;
    });

    // Sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "total_value":
          comparison = a.total_value - b.total_value;
          break;
        case "contacts_count":
          comparison = a.contacts_count - b.contacts_count;
          break;
        case "created_at":
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [accounts, searchQuery, sizeFilter, industryFilter, sortField, sortOrder]);

  const handleEdit = (account: any) => {
    setSelectedAccount(account);
    setEditDialogOpen(true);
    setDetailSheetOpen(false);
  };

  const handleDelete = (account: any) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
    setDetailSheetOpen(false);
  };

  const confirmDelete = async () => {
    if (accountToDelete) {
      await deleteAccount.mutateAsync(accountToDelete.id);
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    }
  };

  const handleViewDetails = (account: any) => {
    setSelectedAccount(account);
    setDetailSheetOpen(true);
  };

  const clearFilters = () => {
    setSizeFilter("all");
    setIndustryFilter("all");
    setSearchQuery("");
  };

  const hasActiveFilters = sizeFilter !== "all" || industryFilter !== "all" || searchQuery;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">Error loading accounts: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Accounts</h1>
          <p className="text-muted-foreground">
            Manage your client organizations ({filteredAccounts.length} total)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-border rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "table" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setViewMode("table")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <AddAccountDialog />
        </div>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl p-4 space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search accounts..."
              className="pl-10 bg-muted/50 border-border"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Button 
            variant={showFilters ? "secondary" : "outline"} 
            className="gap-2"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                !
              </Badge>
            )}
          </Button>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
              <X className="h-3 w-3" />
              Clear
            </Button>
          )}

          <Select value={`${sortField}-${sortOrder}`} onValueChange={(v) => {
            const [field, order] = v.split("-") as [SortField, SortOrder];
            setSortField(field);
            setSortOrder(order);
          }}>
            <SelectTrigger className="w-[180px]">
              <ArrowUpDown className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at-desc">Newest first</SelectItem>
              <SelectItem value="created_at-asc">Oldest first</SelectItem>
              <SelectItem value="name-asc">Name A-Z</SelectItem>
              <SelectItem value="name-desc">Name Z-A</SelectItem>
              <SelectItem value="total_value-desc">Highest value</SelectItem>
              <SelectItem value="total_value-asc">Lowest value</SelectItem>
              <SelectItem value="contacts_count-desc">Most contacts</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {showFilters && (
          <div className="flex items-center gap-4 pt-2 border-t border-border">
            <Select value={sizeFilter} onValueChange={setSizeFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sizes</SelectItem>
                <SelectItem value="micro">Micro</SelectItem>
                <SelectItem value="sme">SME</SelectItem>
                <SelectItem value="mid_market">Mid-Market</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>

            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All industries</SelectItem>
                {industries.map((industry) => (
                  <SelectItem key={industry} value={industry!}>
                    {industry}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        ) : (
          <div className="glass rounded-xl">
            <Skeleton className="h-64" />
          </div>
        )
      ) : !filteredAccounts || filteredAccounts.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-2">No accounts found</p>
          <p className="text-sm text-muted-foreground">
            {hasActiveFilters ? "Try adjusting your filters" : "Add your first account to start tracking client organizations"}
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAccounts.map((account) => (
            <div
              key={account.id}
              className="glass rounded-xl p-5 hover:border-border transition-all duration-200 cursor-pointer group"
              onClick={() => handleViewDetails(account)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{account.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {account.industry || "No industry"}
                    </p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewDetails(account); }}>
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(account); }}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); handleDelete(account); }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="space-y-3">
                {account.website && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Globe className="h-4 w-4" />
                    <span className="truncate">{account.website}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{account.contacts_count} contacts · {account.deals_count} deals</span>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/50">
                <span className={`stage-pill ${sizeColors[account.size_band || "sme"]}`}>
                  {sizeLabels[account.size_band || "sme"]}
                </span>
                <span className="text-sm font-medium text-success">
                  ${account.total_value.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead>Size</TableHead>
                <TableHead className="text-center">Contacts</TableHead>
                <TableHead className="text-center">Deals</TableHead>
                <TableHead className="text-right">Total Value</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAccounts.map((account) => (
                <TableRow 
                  key={account.id} 
                  className="cursor-pointer"
                  onClick={() => handleViewDetails(account)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{account.name}</p>
                        {account.website && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {account.website}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {account.industry || "-"}
                  </TableCell>
                  <TableCell>
                    <span className={`stage-pill ${sizeColors[account.size_band || "sme"]}`}>
                      {sizeLabels[account.size_band || "sme"]}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">{account.contacts_count}</TableCell>
                  <TableCell className="text-center">{account.deals_count}</TableCell>
                  <TableCell className="text-right font-medium text-success">
                    ${account.total_value.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-popover">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewDetails(account); }}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(account); }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => { e.stopPropagation(); handleDelete(account); }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Dialog */}
      <EditAccountDialog
        account={selectedAccount}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />

      {/* Detail Sheet */}
      <AccountDetailSheet
        account={selectedAccount}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{accountToDelete?.name}"? This action cannot be undone.
              All associated contacts and deals may be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
